import type { CommentMention } from "../types.ts";

const MENTION_TOKEN =
	/@\{([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\}/g;

export interface MentionDraft {
	id: string;
	name: string;
}

export interface MentionSegment {
	kind: "text" | "mention";
	start: number;
	text: string;
	id?: string;
}

export function parseMentionSegments(
	body: string,
	mentions: CommentMention[],
): MentionSegment[] {
	const names = new Map(
		mentions.map((mention) => [mention.id.toLowerCase(), mention.name]),
	);
	const segments: MentionSegment[] = [];
	let cursor = 0;
	for (const match of body.matchAll(MENTION_TOKEN)) {
		const raw = match[1];
		if (!raw) continue;
		const index = match.index ?? 0;
		if (index > cursor) {
			segments.push({
				kind: "text",
				start: cursor,
				text: body.slice(cursor, index),
			});
		}
		const id = raw.toLowerCase();
		segments.push({
			kind: "mention",
			start: index,
			id,
			text: names.get(id) ?? "membro",
		});
		cursor = index + match[0].length;
	}
	if (cursor < body.length) {
		segments.push({ kind: "text", start: cursor, text: body.slice(cursor) });
	}
	return segments;
}

export interface MentionQuery {
	start: number;
	query: string;
}

export function mentionQueryAt(
	text: string,
	caret: number,
): MentionQuery | null {
	const upToCaret = text.slice(0, caret);
	const start = upToCaret.lastIndexOf("@");
	if (start === -1) return null;
	const before = upToCaret[start - 1];
	if (before !== undefined && !/[\s([]/.test(before)) return null;
	const query = upToCaret.slice(start + 1);
	if (query.length > 40 || /[@{}\n]/.test(query)) return null;
	return { start, query };
}

export function insertMentionAt(
	text: string,
	start: number,
	caret: number,
	name: string,
): { text: string; caret: number } {
	const inserted = `@${name} `;
	return {
		text: text.slice(0, start) + inserted + text.slice(caret),
		caret: start + inserted.length,
	};
}

function escapeRegExp(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function toWireBody(display: string, drafts: MentionDraft[]): string {
	let wire = display;
	const ordered = [...drafts].sort((a, b) => b.name.length - a.name.length);
	for (const draft of ordered) {
		const pattern = new RegExp(
			`@${escapeRegExp(draft.name)}(?![\\p{L}\\p{N}])`,
			"gu",
		);
		wire = wire.replace(pattern, `@{${draft.id}}`);
	}
	return wire;
}

export function wireMentionIds(wire: string): string[] {
	const ids = new Set<string>();
	for (const match of wire.matchAll(MENTION_TOKEN)) {
		const raw = match[1];
		if (raw) ids.add(raw.toLowerCase());
	}
	return [...ids];
}
